import { Injectable, signal, computed, inject } from '@angular/core';
import { Router } from '@angular/router';
import { AdminRol } from './mock-data.service';
import { ApiService } from './api.service';

export interface AdminUser {
  id: string;
  nombre: string;
  apellido: string;
  rol: AdminRol;
}

const SESSION_KEY  = 'cp_admin_session';
const ACTIVITY_KEY = 'cp_last_activity';
const TIMEOUT_MS   = 30 * 60 * 1000;

@Injectable({ providedIn: 'root' })
export class AdminAuthService {
  private api    = inject(ApiService);
  private router = inject(Router);
  private _user  = signal<AdminUser | null>(null);

  readonly currentUser  = this._user.asReadonly();
  readonly isLoggedIn   = computed(() => this._user() !== null);
  readonly isSuperAdmin = computed(() => this._user()?.rol === 'superadmin');

  constructor() {
    const saved = sessionStorage.getItem(SESSION_KEY);
    if (saved) {
      try {
        if (this.isSessionExpired()) {
          this.logout();
        } else {
          this._user.set(JSON.parse(saved));
        }
      } catch { }
    }
    setInterval(() => {
      if (this._user() && this.isSessionExpired()) {
        this.logout();
        this.router.navigate(['/login']);
      }
    }, 60_000);
  }

  async loginAsync(telefono: string, pin: string): Promise<{ ok: boolean; error?: string }> {
    try {
      const res: any = await this.api.post('/cp/login', { telefono: telefono.trim(), pin });
      if (!res?.ok) return { ok: false, error: res?.mensaje ?? 'Credenciales inválidas.' };
      this.api.setToken(res.token);
      const user: AdminUser = {
        id:       res.admin.id,
        nombre:   res.admin.nombre,
        apellido: res.admin.apellido,
        rol:      res.admin.rol as AdminRol,
      };
      this._user.set(user);
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(user));
      this.refreshActivity();
      return { ok: true };
    } catch {
      return { ok: false, error: 'Error de conexión con el servidor.' };
    }
  }

  login(_t: string, _p: string): { ok: boolean; error?: string } {
    return { ok: false, error: 'Use loginAsync()' };
  }

  logout() {
    this._user.set(null);
    this.api.clearToken();
    sessionStorage.removeItem(SESSION_KEY);
    sessionStorage.removeItem(ACTIVITY_KEY);
  }

  refreshActivity() { sessionStorage.setItem(ACTIVITY_KEY, Date.now().toString()); }

  isSessionExpired(): boolean {
    const last = sessionStorage.getItem(ACTIVITY_KEY);
    if (!last) return true;
    return Date.now() - parseInt(last, 10) > TIMEOUT_MS;
  }
}
